/**
 * PM Work Order Generator
 *
 * Two paths to generate a Work Order:
 * 1. Browse: Select Quote → Select Order → Generate
 * 2. Direct: Type Order Number → Generate
 *
 * UX Principles Applied:
 * - Don Norman: Clear purpose, multiple paths for different mental models
 * - Steve Krug: Don't make users think - obvious options
 * - Luke Wroblewski: Minimal steps, smart defaults
 */

import { useState, useEffect, useRef } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || '';

export default function PMWorkOrderGenerator({ onClose, onShowOtherModules }) {
  // Mode: 'browse' or 'direct'
  const [mode, setMode] = useState('browse');

  // Browse mode state
  const [quotes, setQuotes] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(true);
  const [quoteSearch, setQuoteSearch] = useState('');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showQuoteDropdown, setShowQuoteDropdown] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Direct mode state
  const [directOrderNumber, setDirectOrderNumber] = useState('');

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Recent orders for quick access
  const [recentOrders, setRecentOrders] = useState(() => {
    const saved = localStorage.getItem('pm-recent-orders');
    return saved ? JSON.parse(saved) : [];
  });

  const quoteInputRef = useRef(null);

  // Load quotes on mount
  useEffect(() => {
    loadQuotes();
  }, []);

  // Load orders when quote is selected
  useEffect(() => {
    if (selectedQuote) {
      loadOrdersForQuote(selectedQuote.quoteNumber);
    } else {
      setOrders([]);
      setSelectedOrder(null);
    }
  }, [selectedQuote]);

  const getToken = () => sessionStorage.getItem('msal.backendToken');

  const loadQuotes = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/quotes`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      const data = await response.json();
      // API returns { quotes: [...] }
      setQuotes(data.quotes || []);
    } catch (err) {
      console.error('Failed to load quotes:', err);
    } finally {
      setQuotesLoading(false);
    }
  };

  const loadOrdersForQuote = async (quoteNumber) => {
    setOrdersLoading(true);
    setOrders([]);
    setSelectedOrder(null);
    try {
      const response = await fetch(`${API_BASE}/api/orders?quoteNumber=${encodeURIComponent(quoteNumber)}`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
      });
      const data = await response.json();
      // API returns { orders: [...] }
      const ordersList = data.orders || [];
      setOrders(ordersList);
      // Auto-select if only one order
      if (ordersList.length === 1) {
        setSelectedOrder(ordersList[0]);
      }
    } catch (err) {
      console.error('Failed to load orders:', err);
    } finally {
      setOrdersLoading(false);
    }
  };

  // Filter quotes based on search
  const filteredQuotes = quotes.filter(q => {
    if (!quoteSearch) return true;
    const search = quoteSearch.toLowerCase();
    return (
      q.quoteNumber?.toLowerCase().includes(search) ||
      q.customerName?.toLowerCase().includes(search) ||
      q.user_Text4?.toLowerCase().includes(search) // Project name
    );
  }).slice(0, 20); // Limit for performance

  const handleQuoteSelect = (quote) => {
    setSelectedQuote(quote);
    setQuoteSearch('');
    setShowQuoteDropdown(false);
    setError(null);
    setResult(null);
  };

  const handleOrderSelect = (order) => {
    setSelectedOrder(order);
    setError(null);
    setResult(null);
  };

  const addToRecentOrders = (order) => {
    const updated = [order, ...recentOrders.filter(o => o.orderNumber !== order.orderNumber)].slice(0, 5);
    setRecentOrders(updated);
    localStorage.setItem('pm-recent-orders', JSON.stringify(updated));
  };

  const handleGenerate = async () => {
    const orderNumber = mode === 'browse' ? selectedOrder?.orderNumber : directOrderNumber.trim();

    if (!orderNumber) {
      setError(mode === 'browse' ? 'Please select an order' : 'Please enter an order number');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(`${API_BASE}/api/pm-center/work-orders/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ orderNumber }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate work order');
      }

      setResult(data);

      // Add to recent orders
      addToRecentOrders({
        orderNumber: data.data.orderNumber,
        quoteNumber: data.data.quoteNumber,
        projectName: data.data.projectName,
        generatedAt: new Date().toISOString(),
      });

    } catch (err) {
      setError(err.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleReset = () => {
    setSelectedQuote(null);
    setSelectedOrder(null);
    setDirectOrderNumber('');
    setResult(null);
    setError(null);
  };

  const handleSelectRecent = (order) => {
    setMode('direct');
    setDirectOrderNumber(order.orderNumber);
    setResult(null);
    setError(null);
  };

  const canGenerate = mode === 'browse' ? !!selectedOrder : !!directOrderNumber.trim();

  return (
    <div className="min-h-screen bg-white text-gray-900">
      {/* Header */}
      <header className="bg-slate-100 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-900 transition-colors"
              title="Back to Time Tracker"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h1 className="text-xl font-semibold">Work Order Generator</h1>
          </div>

          {onShowOtherModules && (
            <button
              onClick={onShowOtherModules}
              className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
            >
              Other Modules →
            </button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">

        {/* Mode Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => { setMode('browse'); setError(null); setResult(null); }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                mode === 'browse'
                  ? 'bg-blue-600 text-gray-900'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              Browse Quotes
            </button>
            <button
              onClick={() => { setMode('direct'); setError(null); setResult(null); }}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                mode === 'direct'
                  ? 'bg-blue-600 text-gray-900'
                  : 'text-gray-500 hover:text-gray-900'
              }`}
            >
              Enter Order #
            </button>
          </div>
        </div>

        {/* Browse Mode */}
        {mode === 'browse' && !result && (
          <div className="max-w-lg mx-auto space-y-6">
            {/* Quote Selector */}
            <div>
              <label className="block text-sm text-gray-500 mb-2">1. Select Quote</label>
              <div className="relative">
                <input
                  ref={quoteInputRef}
                  type="text"
                  value={selectedQuote ? `${selectedQuote.quoteNumber} - ${selectedQuote.customerName || ''}` : quoteSearch}
                  onChange={(e) => {
                    setQuoteSearch(e.target.value);
                    setSelectedQuote(null);
                    setShowQuoteDropdown(true);
                  }}
                  onFocus={() => setShowQuoteDropdown(true)}
                  placeholder={quotesLoading ? 'Loading quotes...' : 'Search by quote #, customer, or project...'}
                  className="w-full px-4 py-3 bg-slate-100 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500"
                  disabled={quotesLoading}
                />
                {selectedQuote && (
                  <button
                    onClick={() => { setSelectedQuote(null); setQuoteSearch(''); quoteInputRef.current?.focus(); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-900"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}

                {/* Quote Dropdown */}
                {showQuoteDropdown && !selectedQuote && filteredQuotes.length > 0 && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowQuoteDropdown(false)} />
                    <div className="absolute top-full left-0 right-0 mt-1 bg-slate-100 border border-gray-300 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                      {filteredQuotes.map((quote) => (
                        <button
                          key={quote.quoteNumber}
                          onClick={() => handleQuoteSelect(quote)}
                          className="w-full text-left px-4 py-3 hover:bg-slate-100 transition-colors border-b border-gray-200 last:border-0"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-gray-900">{quote.quoteNumber}</span>
                            <span className="text-sm text-gray-500">{quote.customerName}</span>
                          </div>
                          {quote.user_Text4 && (
                            <div className="text-sm text-gray-500 mt-1">{quote.user_Text4}</div>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Order Selector */}
            {selectedQuote && (
              <div>
                <label className="block text-sm text-gray-500 mb-2">2. Select Order</label>
                {ordersLoading ? (
                  <div className="px-4 py-3 bg-slate-100 border border-gray-300 rounded-lg text-gray-500">
                    Loading orders...
                  </div>
                ) : orders.length === 0 ? (
                  <div className="px-4 py-3 bg-slate-100 border border-gray-300 rounded-lg text-gray-500">
                    No orders found for this quote
                  </div>
                ) : (
                  <div className="space-y-2">
                    {orders.map((order) => (
                      <button
                        key={order.orderNumber}
                        onClick={() => handleOrderSelect(order)}
                        className={`w-full text-left p-4 rounded-lg border transition-colors ${
                          selectedOrder?.orderNumber === order.orderNumber
                            ? 'bg-blue-100 border-blue-500'
                            : 'bg-slate-100 border-gray-300 hover:border-gray-500'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">{order.orderNumber}</span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            order.status === 'Active' ? 'bg-green-900 text-green-600' :
                            order.status === 'Complete' ? 'bg-gray-700 text-gray-500' :
                            'bg-yellow-900 text-yellow-600'
                          }`}>
                            {order.status || 'Unknown'}
                          </span>
                        </div>
                        {order.projectName && (
                          <div className="text-sm text-gray-500 mt-1">{order.projectName}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Generate Button */}
            {selectedQuote && (
              <button
                onClick={handleGenerate}
                disabled={!canGenerate || isGenerating}
                className={`w-full py-3 rounded-lg text-lg font-medium transition-colors ${
                  !canGenerate || isGenerating
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-gray-900'
                }`}
              >
                {isGenerating ? 'Generating...' : 'Generate Work Order'}
              </button>
            )}
          </div>
        )}

        {/* Direct Mode */}
        {mode === 'direct' && !result && (
          <div className="max-w-md mx-auto space-y-6">
            <div>
              <label className="block text-sm text-gray-500 mb-2">Order Number</label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={directOrderNumber}
                  onChange={(e) => setDirectOrderNumber(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && canGenerate && handleGenerate()}
                  placeholder="e.g., 2000961"
                  className="flex-1 px-4 py-3 bg-slate-100 border border-gray-300 rounded-lg text-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:border-blue-500"
                  autoFocus
                  disabled={isGenerating}
                />
                <button
                  onClick={handleGenerate}
                  disabled={!canGenerate || isGenerating}
                  className={`px-6 py-3 rounded-lg text-lg font-medium transition-colors ${
                    !canGenerate || isGenerating
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700 text-gray-900'
                  }`}
                >
                  {isGenerating ? 'Generating...' : 'Generate'}
                </button>
              </div>
            </div>

            {/* Recent Orders */}
            {recentOrders.length > 0 && (
              <div>
                <h3 className="text-sm text-gray-500 uppercase tracking-wide mb-3">Recent</h3>
                <div className="space-y-2">
                  {recentOrders.map((order) => (
                    <button
                      key={order.orderNumber}
                      onClick={() => handleSelectRecent(order)}
                      className="w-full text-left p-3 bg-slate-100 hover:bg-gray-750 border border-gray-200 hover:border-gray-300 rounded-lg transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-gray-900 font-medium">{order.orderNumber}</span>
                          <span className="text-gray-500 mx-2">·</span>
                          <span className="text-gray-500">Quote {order.quoteNumber}</span>
                        </div>
                        <span className="text-gray-500 text-sm truncate max-w-[150px]">
                          {order.projectName || ''}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="max-w-md mx-auto mt-6 p-4 bg-red-100 border border-red-700 rounded-lg">
            <div className="flex items-start gap-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-red-600 font-medium">Error</p>
                <p className="text-red-400 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Success State */}
        {result && result.success && (
          <div className="max-w-lg mx-auto p-6 bg-green-50 border border-green-700 rounded-lg">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-green-600 rounded-full flex-shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-green-600 font-semibold text-lg">Work Order Generated!</p>

                <div className="mt-3 space-y-1 text-sm">
                  <p><span className="text-gray-500">Quote:</span> <span className="text-gray-900">{result.data.quoteNumber}</span></p>
                  <p><span className="text-gray-500">Order:</span> <span className="text-gray-900">{result.data.orderNumber}</span></p>
                  <p><span className="text-gray-500">Project:</span> <span className="text-gray-900">{result.data.projectName || 'N/A'}</span></p>
                  <p><span className="text-gray-500">PM:</span> <span className="text-gray-900">{result.data.projectManager || 'N/A'}</span></p>
                  <p><span className="text-gray-500">SKUs:</span> <span className="text-gray-900">{result.data.skuCount}</span></p>
                </div>

                <div className="mt-4 flex gap-3">
                  <a
                    href={result.sharepointUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-gray-900 font-medium transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    Open in SharePoint
                  </a>
                  <button
                    onClick={handleReset}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-700 transition-colors"
                  >
                    Generate Another
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
